import { useCallback, useLayoutEffect, useState } from 'react';

export const DROPDOWN_Z_INDEX = 10020;
export const LIGHTBOX_Z_INDEX = 10050;
export const DROPDOWN_MAX_HEIGHT = 240;
export const DROPDOWN_VIEWPORT_MARGIN = 8;
export const DROPDOWN_OPTION_ESTIMATED_HEIGHT = 44;

export function estimateDropdownHeight(optionCount, padding = 12) {
  return Math.min(optionCount * DROPDOWN_OPTION_ESTIMATED_HEIGHT + padding, DROPDOWN_MAX_HEIGHT);
}

export function computeFloatingDropdownPosition({
  anchorRect,
  panelHeight,
  gap = 4,
  minWidth = 140,
}) {
  const margin = DROPDOWN_VIEWPORT_MARGIN;
  const spaceBelow = window.innerHeight - anchorRect.bottom - margin;
  const spaceAbove = anchorRect.top - margin;
  const contentHeight = Math.min(panelHeight, DROPDOWN_MAX_HEIGHT);
  const needed = contentHeight + gap;

  const fitsBelow = spaceBelow >= needed;
  const fitsAbove = spaceAbove >= needed;

  let placement = 'bottom';
  if (!fitsBelow && (fitsAbove || spaceAbove > spaceBelow)) {
    placement = 'top';
  }

  const maxHeight =
    placement === 'bottom'
      ? Math.max(96, Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow - gap))
      : Math.max(96, Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove - gap));

  const visibleHeight = Math.min(contentHeight, maxHeight);
  const top =
    placement === 'bottom'
      ? anchorRect.bottom + gap
      : Math.max(margin, anchorRect.top - visibleHeight - gap);

  return {
    top,
    left: anchorRect.left,
    width: Math.max(anchorRect.width, minWidth),
    placement,
    maxHeight,
  };
}

export function useFloatingPanelPosition(open, gap = 4) {
  const [anchorRef, setAnchorRef] = useState(null);
  const [style, setStyle] = useState({ top: 0, left: 0, width: 0 });

  const refCallback = useCallback((node) => {
    setAnchorRef(node);
  }, []);

  useLayoutEffect(() => {
    if (!open || !anchorRef) return undefined;

    function update() {
      const rect = anchorRef.getBoundingClientRect();
      setStyle({
        top: rect.bottom + gap,
        left: rect.left,
        width: rect.width,
      });
    }

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, gap, anchorRef]);

  return { anchorRef: refCallback, style };
}

export function useFloatingDropdownPosition({
  open,
  anchorRef,
  panelRef,
  itemCount = 1,
  gap = 4,
  minWidth = 140,
}) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setPosition(null);
      return undefined;
    }

    function update() {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const panelEl = panelRef?.current;
      const measuredHeight = panelEl
        ? Math.min(panelEl.scrollHeight, DROPDOWN_MAX_HEIGHT)
        : estimateDropdownHeight(itemCount);

      setPosition(
        computeFloatingDropdownPosition({
          anchorRect,
          panelHeight: measuredHeight,
          gap,
          minWidth,
        })
      );
    }

    update();
    const raf = window.requestAnimationFrame(update);

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, panelRef, itemCount, gap, minWidth]);

  return position;
}
